import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import VelavuSlider from "../../../../elements/velavu-slider";
import { VelavuAPI, VelavuSite } from "velavu-js-api";
import VelavuButton from "../../../../elements/velavu-button";
import VelavuInput from "../../../../elements/velavu-input";
import {
	requestAddressReverseGeocoding,
	useMapboxAPIKey,
} from "../../../../helper/api-helper";
import { useSignInContext } from "../../../sign-in-context/sign-in-context";
import PinnedList from "../pinned-list";
import PinnedListHeader from "../pinned-list-header";
import SiteCreateSearch, {
	SiteCreateAddressResult,
} from "./site-create-search";
import * as styles from "./site-create.module.scss";
import VelavuNotice from "../../../../elements/velavu-notice/velavu-notice";
import IconInfo from "../../../../dynamicicons/icon-info";
import VelavuIconButton from "../../../../elements/velavu-icon-button";
import whitelabel from "../../../../whitelabel";
import { useMappedinIntegrationContext } from "../../../main/mappedin-integration-provider";

interface SiteCreateProps {
	from: string;
	//Pass if editing an existing site
	initialEdit?: VelavuSite;
	//Pass if creating new site by clicking on map
	initialCoords?: [number, number];
	onClose?: VoidFunction;
	onPreview?: (coords: [number, number]) => void;
	onResult?: (site: VelavuSite) => void;
}

export default function SiteCreate(props: SiteCreateProps) {
	const { t, i18n } = useTranslation();
	const [inputSiteName, setInputSiteName] = useState(
		props.initialEdit?.name ?? "",
	);
	const [inputAddress, setInputAddress] = useState<
		SiteCreateAddressResult | undefined
	>(undefined);
	const inputGeocodingRequested = useRef(false);
	const [siteBoundary, setSiteBoundary] = useState(
		((props.initialEdit?.boundary_factor ?? 1.5) / 3) * 4,
	);

	const coords = props.initialCoords ?? props.initialEdit?.coordinates;
	const mapboxToken = useMapboxAPIKey();
	const mapboxLanguage = i18n.language;
	const { siteVenueMap } = useMappedinIntegrationContext();
	const isMappedinImportedSite =
		props.initialEdit !== undefined &&
		siteVenueMap.has(props.initialEdit.id);
	if (!inputGeocodingRequested.current && coords !== undefined) {
		if (mapboxToken !== undefined) {
			requestAddressReverseGeocoding(
				mapboxToken,
				mapboxLanguage,
				coords,
			).then((address) => {
				if (address !== undefined) {
					setInputAddress({
						id: "",
						name: address,
						position: coords,
					});
				}
			});
			inputGeocodingRequested.current = true;
		}
	}

	const propsOnPreview = props.onPreview;
	const updateInputAddress = useCallback(
		(address: SiteCreateAddressResult | undefined) => {
			setInputAddress(address);
			if (address !== undefined && propsOnPreview !== undefined)
				propsOnPreview(address.position);
		},
		[setInputAddress, propsOnPreview],
	);

	const [isLoading, setLoading] = useState(false);

	const inputValid =
		inputSiteName.trim().length > 0 && inputAddress !== undefined;

	const propsInitialEdit = props.initialEdit;
	const propsOnResult = props.onResult;
	const submitSite = useCallback(() => {
		setLoading(true);

		const boundary_factor = (siteBoundary / 4) * 3;

		(propsInitialEdit
			? VelavuAPI.sites.modifyExistingSite(propsInitialEdit.id, {
					name: inputSiteName,
					coordinates: inputAddress!.position,
					boundary_factor,
				})
			: VelavuAPI.sites.createNewSite({
					name: inputSiteName,
					coordinates: inputAddress!.position,
					boundary_factor,
				})
		)
			.then(propsOnResult ?? (() => {}))
			.catch((error) => {
				console.log(error.response);
				setLoading(false);
			});
	}, [
		propsInitialEdit,
		inputSiteName,
		inputAddress,
		siteBoundary,
		setLoading,
		propsOnResult,
	]);

	return (
		<PinnedList>
			<PinnedListHeader
				label={
					props.initialEdit
						? t("dashboard.site_create.header_edit")
						: t("dashboard.site_create.header_new")
				}
				onClose={props.onClose}
			/>

			<div className={styles.container}>
				<span className={styles.label}>
					{t("dashboard.site_create.name")}
				</span>
				<VelavuInput
					value={inputSiteName}
					onChange={setInputSiteName}
					bottomPadding
					fullWidth
				/>

				<span className={styles.label}>
					{t("dashboard.site_create.address")}
				</span>
				<SiteCreateSearch
					from={props.from}
					address={inputAddress}
					onSetAddress={updateInputAddress}
					disabled={isMappedinImportedSite}
				/>

				<div className={styles.labelRow}>
					<span>{t("dashboard.site_create.site_boundary")}</span>
					<VelavuIconButton
						tooltip={t(
							"dashboard.site_create.site_boundary_tooltip",
						)}
						tooltipStyle={{
							top: 30,
						}}
						tooltipType="paragraph"
					>
						<IconInfo
							size={16}
							color={whitelabel.palette.primary800}
						/>
					</VelavuIconButton>
				</div>
				<VelavuSlider
					min={0}
					max={4}
					value={siteBoundary}
					onChange={setSiteBoundary}
					variant="marked"
				/>
				<span className={styles.sliderLabel}>
					{t("dashboard.site_create.boundary_default")}
				</span>
				<VelavuNotice style={{ marginTop: 6 }} type="info">
					{t("dashboard.site_create.boundary_notice")}
				</VelavuNotice>

				{props.initialEdit ? (
					<div className={styles.buttonBar}>
						<VelavuButton
							label={t("common.cancel")}
							fullWidth
							variant="outlined"
							disabled={isLoading}
							onClick={props.onClose}
						/>

						<VelavuButton
							label={t("common.save")}
							fullWidth
							disabled={!inputValid || isLoading}
							loadingIndicator={isLoading}
							onClick={submitSite}
						/>
					</div>
				) : (
					<VelavuButton
						className={styles.buttonSubmit}
						label={t("common.create_site")}
						fullWidth
						disabled={!inputValid || isLoading}
						loadingIndicator={isLoading}
						onClick={submitSite}
					/>
				)}
			</div>
		</PinnedList>
	);
}
